import type {
  SectionCompletion,
  SectionView,
  SetSectionCompletionRequest,
} from '@assessments/shared'
import { apiFetch } from '../../services/apiClient.js'
import { assertOk } from '../../services/apiError.js'

/** Fetches the aggregated, read-only section-view payload for a section. */
export async function fetchSectionView(
  organizationSurveyId: string,
  sectionId: string,
): Promise<SectionView> {
  const res = await apiFetch(
    `/assessments/${encodeURIComponent(organizationSurveyId)}/sections/${encodeURIComponent(sectionId)}`,
  )
  if (!res.ok) {
    throw new Error(`Failed to load section (${res.status})`)
  }
  return (await res.json()) as SectionView
}

const completionPath = (organizationSurveyId: string, sectionId: string) =>
  `/assessments/${encodeURIComponent(organizationSurveyId)}/sections/${encodeURIComponent(sectionId)}/completion`

/** Reads a section's current completion state (EA-62). */
export async function fetchSectionCompletion(
  organizationSurveyId: string,
  sectionId: string,
): Promise<SectionCompletion> {
  const res = await apiFetch(completionPath(organizationSurveyId, sectionId))
  if (!res.ok) throw new Error(`Failed to load completion state (${res.status})`)
  return (await res.json()) as SectionCompletion
}

/**
 * The required-answered gate rejected a mark-complete (HTTP 422, FR-011). Carries the
 * outstanding required question ids so the section view can tell the respondent which
 * remain — distinct from a transport/persistence failure.
 */
export class CompletionValidationError extends Error {
  constructor(public readonly unansweredRequiredQuestionIds: string[]) {
    super('Required questions are unanswered')
    this.name = 'CompletionValidationError'
  }
}

/**
 * Transitions a section's completion state (EA-62). Throws CompletionValidationError
 * on a 422 required-answered rejection; an ApiError on any other failure, so a caller
 * can tell a dead session from a persistence problem.
 */
export async function setSectionCompletion(
  organizationSurveyId: string,
  sectionId: string,
  state: SetSectionCompletionRequest['state'],
): Promise<SectionCompletion> {
  const res = await apiFetch(completionPath(organizationSurveyId, sectionId), {
    method: 'PUT',
    body: JSON.stringify({ state }),
  })
  if (res.status === 422) {
    const body = (await res.json().catch(() => ({}))) as {
      unansweredRequiredQuestionIds?: string[]
    }
    throw new CompletionValidationError(body.unansweredRequiredQuestionIds ?? [])
  }
  await assertOk(res, 'update completion state')
  return (await res.json()) as SectionCompletion
}
